// Which minutes of the show have something scheduled, worked out from the
// windows alone.
//
// WHY THIS EXISTS WHEN player/rehearse.ts ALREADY REPORTS DARK TIME.
//
// The rehearsal is the better answer to "what will the screen do", because it
// runs the real rotation rather than modelling it, and it is the only thing
// that can tell anybody a twenty second window under a four minute rotation is
// never seen. It has two properties that make it the wrong basis for a refusal.
// It needs instants, so it can only ever answer for a span somebody chose. And
// it assumes every item loads and every video is however long the caller said,
// so its answer is evidence about a day rather than proof about a schedule.
//
// This file answers a smaller question completely: given these windows and
// these opening hours, is there a minute of the show with nothing eligible? No
// clock, no instants, no assumptions about loading, and no rotation: set
// arithmetic on wall clock readings. That is provable, so it is the thing a
// refusal is built on, and the rehearsal stays where it belongs, which is
// advice.
//
// EVERYTHING HERE IS A WALL CLOCK READING AND NOTHING IS CONVERTED.
//
// contract/time.ts turns an instant into the venue's wall clock and refuses to
// go the other way, because the other way is ambiguous twice a year. This file
// never needs to: a date in the venue's calendar and a time on the venue's
// clock are both strings, and "the nineteenth of the month, ten past nine" is a
// fact about the show whether or not anybody can say which instant it was. The
// cost is the same two hours a year the rest of the package names: a day with a
// daylight saving changeover in it does not have 86,400 seconds, and this file
// says it does. For a show running nine until six that is one hour nobody is in
// the hall for.

import type { Bound } from "../contract/time.js";
import { readBound } from "../contract/time.js";

/** A stretch of one day, in seconds from midnight, half open. */
export type Span = { readonly from: number; readonly until: number };

export const DAY_SECONDS = 86_400;

/**
 * The longest run of days this will walk.
 *
 * A span of years is a typo, and the one that actually happens is a year
 * mistyped in `closesOn`, which without this would walk hundreds of days of
 * set arithmetic before saying anything. A year is longer than a season-long
 * activation and shorter than that mistake. The cap is reported rather than
 * silently applied, so a mistyped year is a refusal about the year instead of
 * a schedule that looks fine for the days somebody happened to look at.
 */
export const MAX_SHOW_DAYS = 366;

/** Seconds from midnight for `HH:MM` or `HH:MM:SS`, or null if it is not a time of day. */
export function secondsOfDay(text: string): number | null {
  const bound = readBound(text.trim());
  if (bound === null || bound.shape !== "daily") return null;
  return clockSeconds(bound.at);
}

const clockSeconds = (hhmmss: string): number =>
  Number(hhmmss.slice(0, 2)) * 3_600 + Number(hhmmss.slice(3, 5)) * 60 + Number(hhmmss.slice(6, 8));

const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Whether a string is a date in the venue's calendar, `YYYY-MM-DD`. */
export function isDate(text: string): boolean {
  const found = DATE.exec(text.trim());
  if (found === null) return false;
  const [, year, month, day] = found;
  return realDate(Number(year), Number(month), Number(day));
}

// Written out rather than handed to Date for the reason contract/time.ts gives:
// Date accepts the thirty-first of February and rolls it into March, and a show
// that silently starts on a different day is worse than one that is refused.
function realDate(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1) return false;
  const lengths = [31, leap(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= (lengths[month - 1] ?? 0);
}

const leap = (year: number): boolean => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

/** The day after this one, in the venue's calendar. */
export function nextDate(date: string): string {
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7));
  const day = Number(date.slice(8, 10));

  if (realDate(year, month, day + 1)) return `${pad4(year)}-${pad2(month)}-${pad2(day + 1)}`;
  if (month < 12) return `${pad4(year)}-${pad2(month + 1)}-01`;
  return `${pad4(year + 1)}-01-01`;
}

const pad2 = (value: number): string => String(value).padStart(2, "0");
const pad4 = (value: number): string => String(value).padStart(4, "0");

/**
 * Every day the show runs, inclusive of both ends.
 *
 * Empty where the dates are not dates or run backwards, and capped at
 * MAX_SHOW_DAYS with the cap reported rather than silently applied, so that a
 * mistyped year is a refusal about the year instead of a schedule that looks
 * fine for the days somebody happened to look at.
 */
export function showDays(opensOn: string, closesOn: string): { days: readonly string[]; overran: boolean } {
  if (!isDate(opensOn) || !isDate(closesOn) || closesOn < opensOn) return { days: [], overran: false };

  const days: string[] = [];
  let day = opensOn.trim();
  while (day <= closesOn.trim()) {
    if (days.length >= MAX_SHOW_DAYS) return { days, overran: true };
    days.push(day);
    day = nextDate(day);
  }
  return { days, overran: false };
}

/**
 * When an item is eligible on one day, as spans of that day.
 *
 * The half-open reading of a window from contract/time.ts, restated as
 * arithmetic: an item joins at its start and has gone by its stop. A daily
 * window whose stop is before its start wraps midnight and lands on the day as
 * two spans, which is the reading `windowOpen` takes and the only sane one for
 * "from ten at night until two".
 *
 * A window with a shape mismatch or a backwards dated pair returns nothing.
 * Both are refused elsewhere with a sentence of their own; returning nothing
 * here means a draft carrying one is never also accused of leaving the screen
 * dark, which would be two refusals for one mistake.
 */
export function spansOn(from: Bound | null, until: Bound | null, day: string): readonly Span[] {
  const whole: readonly Span[] = [{ from: 0, until: DAY_SECONDS }];

  if (from === null && until === null) return whole;
  if (from !== null && until !== null && from.shape !== until.shape) return [];

  if ((from ?? until)?.shape === "daily") {
    const start = from === null ? 0 : clockSeconds(from.at);
    const stop = until === null ? DAY_SECONDS : clockSeconds(until.at);
    if (stop === start) return [];
    if (stop > start) return [{ from: start, until: stop }];
    // Wraps midnight: the tail of the day and the head of it.
    return [{ from: 0, until: stop }, { from: start, until: DAY_SECONDS }];
  }

  const startDay = from === null ? null : from.at.slice(0, 10);
  const stopDay = until === null ? null : until.at.slice(0, 10);
  if (startDay !== null && stopDay !== null && until !== null && from !== null && until.at <= from.at) return [];

  if (startDay !== null && day < startDay) return [];
  if (stopDay !== null && day > stopDay) return [];

  const start = startDay === day && from !== null ? clockSeconds(from.at.slice(11)) : 0;
  const stop = stopDay === day && until !== null ? clockSeconds(until.at.slice(11)) : DAY_SECONDS;
  return stop > start ? [{ from: start, until: stop }] : [];
}

/** Overlapping spans merged into the fewest that cover the same seconds. */
export function merge(spans: readonly Span[]): readonly Span[] {
  const sorted = [...spans].filter((span) => span.until > span.from).sort((a, b) => a.from - b.from);
  const out: Span[] = [];

  for (const span of sorted) {
    const last = out[out.length - 1];
    // Touching counts as overlapping: a window ending at 13:00 and the next
    // starting at 13:00 hand over without a gap, which is what half-open
    // windows are for, and a gap of zero seconds reported as darkness would
    // refuse every correctly written handover in the estate.
    if (last !== undefined && span.from <= last.until) {
      if (span.until > last.until) out[out.length - 1] = { from: last.from, until: span.until };
      continue;
    }
    out.push(span);
  }

  return out;
}

/** The parts of `within` that none of `covered` reaches. */
export function gapsIn(within: Span, covered: readonly Span[]): readonly Span[] {
  const gaps: Span[] = [];
  let at = within.from;

  for (const span of merge(covered)) {
    if (span.until <= within.from) continue;
    if (span.from >= within.until) break;
    if (span.from > at) gaps.push({ from: at, until: Math.min(span.from, within.until) });
    at = Math.max(at, span.until);
    if (at >= within.until) break;
  }

  if (at < within.until) gaps.push({ from: at, until: within.until });
  return gaps;
}

/** `HH:MM` for a reading in seconds, for putting in a sentence somebody reads. */
export const clockOf = (seconds: number): string =>
  `${pad2(Math.floor(seconds / 3_600) % 24)}:${pad2(Math.floor(seconds / 60) % 60)}`;

/**
 * How far apart two wall clock readings are, in seconds.
 *
 * ARITHMETIC ON A CALENDAR, NOT A CONVERSION TO AN INSTANT, and the difference
 * matters enough to be worth the paragraph. Turning `2026-09-01T12:00:00` into
 * a moment in time needs a timezone and is ambiguous twice a year, which is why
 * contract/time.ts refuses to do it. Counting the days and seconds between two
 * readings on the same calendar needs neither: it is the answer a person gives
 * when asked how long there is between noon and half past one.
 *
 * It is wrong by an hour across a daylight saving changeover, in the same way
 * and for the same reason as everything else in this module, and it is used for
 * one thing, saying how long the rotation takes to come round, where an hour
 * once a year is not a number anybody acts on.
 */
export const apartSeconds = (earlier: string, later: string): number => stampSeconds(later) - stampSeconds(earlier);

const stampSeconds = (stamp: string): number => daysFromCivil(stamp.slice(0, 10)) * DAY_SECONDS + clockSeconds(stamp.slice(11));

/**
 * Whole days from the first of January 1970 for a date in the venue's calendar.
 *
 * Howard Hinnant's `days_from_civil`, which is integer arithmetic over the
 * proleptic Gregorian calendar and touches no clock, no zone and no `Date`.
 * Written out rather than reached for because `new Date("2026-09-01")` parses
 * as UTC midnight, which is the previous day in half the world, and a rotation
 * reported as coming round twice as often as it does would be believed.
 */
export function daysFromCivil(date: string): number {
  const y0 = Number(date.slice(0, 4));
  const m = Number(date.slice(5, 7));
  const d = Number(date.slice(8, 10));

  const y = y0 - (m <= 2 ? 1 : 0);
  const era = Math.floor(y / 400);
  const yoe = y - era * 400;
  const doy = Math.floor((153 * (m + (m > 2 ? -3 : 9)) + 2) / 5) + d - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146_097 + doe - 719_468;
}
